import vkBridge, {
	EAdsFormats,
	type ReceiveData,
	type RequestPropsMap
} from "@vkontakte/vk-bridge";
import OpenApiClient from "../Api/OpenApiClient";
import { getAuthToken } from "../Api/Auth";
import {
	EFxIdSdkAdapterInfoSocialCapability,
	EFxIdSdkGender,
	FxIdSdkAdapterSocialSettingDefault,
	FxIdSdkBaseAdapter,
	type IExportedFxIdSdkMethod,
	type IFxIdSdkAdapterBuyProductRequest,
	type IFxIdSdkAdapterBuyProductResponse,
	type IFxIdSdkAdapterGetFriendsRequest,
	type IFxIdSdkAdapterInfoSocial,
	type IFxIdSdkAdapterInviteFriendsRequest,
	type IFxIdSdkAdapterSocialSettings,
	type IFxIdSdkAdapterStatEventRequest,
	type IFxIdSdkAdapterStatInitializeRequest,
	type IFxIdSdkGetFriendsResult,
	type IFxIdSdkInviteFriendsResult
} from "./FxIdSdkBaseAdapter";
import {
	FxIdApplicationStoreCreatePaymentHandlerEmbeddingType,
	FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	type FxGamesLibWebPublicDataBase,
	type FxIdWebFeaturesStoreCreatePaymentRequest
} from "../Api/gen";
import { sharingHandlersStore, userStore } from "../Stores";
import type { IframeType } from "../Screens/WebPlayer/Hooks";
import { PlatformPurchaseError, runPlatformPurchaseStep, type IPlatformPurchaseContext } from "./PlatformPurchaseError";
import { createVkBridgePurchaseDescriptor } from "./VkBridgePurchase";

const okWebPlatformsWithRequestKey = ["desktop_web_ok", "mobile_web_ok"];
const okUidPattern = /^[1-9]\d*$/;
/** Диагностика окна оплаты VK Bridge для Одноклассников через кабинет ВК (#fxid-1856) */
const OK_VIA_VK_BRIDGE_PURCHASE = createVkBridgePurchaseDescriptor("ok");

type LocalOkProfileRequest = RequestPropsMap["VKWebAppGetUserInfo"] & {
	use_local: true;
};

type OkInviteRequest = RequestPropsMap["VKWebAppShowInviteBox"] & {
	message?: string;
	requestKey?: string;
	request_id?: string | number;
};

export class FxIdSdkAdapterForOkViaVkBridge extends FxIdSdkBaseAdapter {
	readonly adapter: IframeType = "ok";
	readonly defaultSocialSettings: IFxIdSdkAdapterSocialSettings = {
		...FxIdSdkAdapterSocialSettingDefault,
		ui: { ...FxIdSdkAdapterSocialSettingDefault.ui, disabled: false }
	};

	/** undefined — профиль ещё не запрошен; null — Bridge вернул профиль другого пользователя. */
	private cachedProfile?: ReceiveData<"VKWebAppGetUserInfo"> | null;
	private rewardedVideoAvailable = false;
	private interstitialVideoAvailable = false;

	constructor(
		protected exportedSdk: IExportedFxIdSdkMethod,
		protected game: string,
		protected config: FxGamesLibWebPublicDataBase
	) {
		super(exportedSdk, game, config);
	}

	private getOkLaunchUserId(): string {
		const userId = new URLSearchParams(location.search).get("vk_ok_user_id");
		if (userId == null || !okUidPattern.test(userId)) {
			throw new Error("Missing vk_ok_user_id in Odnoklassniki launch parameters");
		}
		return userId;
	}

	/**
	 * Профиль Bridge нужен только для витрины: имени, пола и аватара. Игрок ОК определяется
	 * подписанным vk_ok_user_id, который проверяет сервер. Приложение ОК (Android/iOS) на
	 * use_local отдаёт профиль другого пользователя (fxid-1821), поэтому несовпадение id
	 * не прерывает запуск: чужой профиль просто не показывается. Ошибка самого запроса
	 * по-прежнему всплывает — по ней Initialize понимает, что Bridge недоступен.
	 */
	private async loadLocalOkProfile(): Promise<ReceiveData<"VKWebAppGetUserInfo"> | null> {
		const expectedId = this.getOkLaunchUserId();
		const request: LocalOkProfileRequest = { use_local: true };
		const profile = await vkBridge.send("VKWebAppGetUserInfo", request);
		return String(profile.id) === expectedId ? profile : null;
	}

	async Initialize(): Promise<void> {
		const initialized = await vkBridge.send("VKWebAppInit", {});
		if (!initialized.result) {
			log.warn("VK Bridge init did not return result=true, readiness is verified by the profile request");
		}

		const social = await this.GetSocialInfo();
		userStore.getState().updateUserId(social.userId);
		userStore.getState().updateDisplayName(`#${social.userId}`);
		void this.CheckAdsAvailable();
	}

	async GetSocialInfo(): Promise<IFxIdSdkAdapterInfoSocial> {
		if (this.cachedProfile === undefined) {
			this.cachedProfile = await this.loadLocalOkProfile();
		}
		return {
			social: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Odnoklassniki,
			paymentsAvailable: true,
			userId: this.getOkLaunchUserId(),
			...this.mapProfileDisplay(this.cachedProfile),
			capabilities: [
				EFxIdSdkAdapterInfoSocialCapability.FriendsList,
				EFxIdSdkAdapterInfoSocialCapability.FriendsInvite,
				EFxIdSdkAdapterInfoSocialCapability.AdsAvailable
			]
		};
	}

	/** Витринные поля из профиля Bridge; без подтверждённого профиля игра получает только uid. */
	private mapProfileDisplay(
		profile: ReceiveData<"VKWebAppGetUserInfo"> | null
	): Pick<IFxIdSdkAdapterInfoSocial, "firstName" | "lastName" | "gender" | "photo"> {
		if (profile == null) {
			return {};
		}
		return {
			firstName: profile.first_name,
			lastName: profile.last_name,
			gender: this.mapOkGender(profile.sex),
			photo: { width: 200, height: 200, url: profile.photo_200 }
		};
	}

	private mapOkGender(sex: number): EFxIdSdkGender {
		if (sex === 1) return EFxIdSdkGender.Female;
		if (sex === 2) return EFxIdSdkGender.Male;
		return EFxIdSdkGender.Unspecified;
	}

	override async GetFriends(_request: IFxIdSdkAdapterGetFriendsRequest): Promise<IFxIdSdkGetFriendsResult> {
		const selected = await vkBridge.send("VKWebAppGetFriends", { multi: true });
		return {
			friends: selected.users.map((user) => ({
				uid: String(user.id),
				firstName: user.first_name,
				lastName: user.last_name,
				gender: this.mapOkGender(user.sex),
				photo: { width: 200, height: 200, url: user.photo_200 }
			}))
		};
	}

	override async InviteFriends(request: IFxIdSdkAdapterInviteFriendsRequest): Promise<IFxIdSdkInviteFriendsResult> {
		const platform = new URLSearchParams(location.search).get("vk_platform") ?? "";
		const inviteRequest: OkInviteRequest = { message: request.inviteText };
		if (request.trackString != null && okWebPlatformsWithRequestKey.includes(platform)) {
			inviteRequest.requestKey = request.trackString;
		}
		const result = await vkBridge.send("VKWebAppShowInviteBox", inviteRequest);
		return { friends: this.normalizeInvitedOkUids(result).map((uid) => ({ uid })) };
	}

	private normalizeInvitedOkUids(response: unknown): string[] {
		if (typeof response !== "object" || response == null || !("uids" in response) || !Array.isArray(response.uids)) {
			return [];
		}
		return response.uids.flatMap((uid) => {
			if (typeof uid === "string" && okUidPattern.test(uid)) return [uid];
			if (typeof uid === "number" && Number.isSafeInteger(uid) && uid > 0) return [String(uid)];
			return [];
		});
	}

	RegisterShareHandlers(): Promise<void> {
		sharingHandlersStore.getState().enableShareOk(() => {
			void vkBridge.send("VKWebAppShare", {}).catch((error) => log.error(error));
		});
		return Promise.resolve();
	}

	async CheckAdsAvailable(): Promise<void> {
		this.rewardedVideoAvailable = await this.probeNativeAds(EAdsFormats.REWARD);
		if (this.rewardedVideoAvailable) window.FxIdSdk!.DispatchAdsVideoAvailable();
		this.interstitialVideoAvailable = await this.probeNativeAds(EAdsFormats.INTERSTITIAL);
		if (this.interstitialVideoAvailable) window.FxIdSdk!.DispatchAdsInterstitialAvailable();
	}

	private async probeNativeAds(format: EAdsFormats): Promise<boolean> {
		try {
			return (await vkBridge.send("VKWebAppCheckNativeAds", { ad_format: format })).result;
		} catch (error) {
			log.error(error);
			return false;
		}
	}

	override AdsIsVideoReady(): Promise<boolean> {
		void this.CheckAdsAvailable();
		return Promise.resolve(this.rewardedVideoAvailable);
	}

	override AdsIsInterstitialReady(): Promise<boolean> {
		return Promise.resolve(this.interstitialVideoAvailable);
	}

	override AdsShowVideo(): Promise<void> {
		return this.showNativeAds(EAdsFormats.REWARD);
	}

	override AdsShowInterstitial(): Promise<void> {
		return this.showNativeAds(EAdsFormats.INTERSTITIAL);
	}

	private async showNativeAds(format: EAdsFormats): Promise<void> {
		try {
			const result = await vkBridge.send("VKWebAppShowNativeAds", { ad_format: format });
			if (result.result) {
				this.exportedSdk.DispatchAdsFinished();
			} else {
				this.exportedSdk.DispatchAdsFailed();
			}
		} catch (error) {
			log.error(error);
			this.exportedSdk.DispatchAdsFailed();
		}
	}

	StoreCurrency(): Promise<string | undefined> {
		return Promise.resolve(undefined);
	}

	StatInitialize(_request: IFxIdSdkAdapterStatInitializeRequest): Promise<void> {
		return Promise.resolve();
	}

	StatEvent(_request: IFxIdSdkAdapterStatEventRequest): Promise<void> {
		return Promise.resolve();
	}

	/**
	 * Создаёт заказ FX ID и открывает оплату Bridge, сохраняя полную диагностику отказа для игры
	 * @param request товар и параметры покупки, переданные игрой
	 */
	async BuyProduct(request: IFxIdSdkAdapterBuyProductRequest): Promise<IFxIdSdkAdapterBuyProductResponse> {
		const paymentRequest: FxIdWebFeaturesStoreCreatePaymentRequest = {
			Token: getAuthToken()!,
			Game: this.game,
			Sku: request.sku,
			WebPublishingPlatform: FxIdDomainStoreEnumsSupportedWebPublishingPlatform.Odnoklassniki,
			EmbeddingType: FxIdApplicationStoreCreatePaymentHandlerEmbeddingType.Embed,
			ProductDescriptionHint: request.productDescriptionHint,
			ProductNameHint: request.productNameHint,
			LaunchQueryString: location.search
		};
		if (request.developerPayload != null) {
			paymentRequest.DeveloperPayload = { MerchantDeveloperPayload: request.developerPayload };
		}

		const payment = await runPlatformPurchaseStep(
			OK_VIA_VK_BRIDGE_PURCHASE,
			"create_payment",
			{ sku: request.sku },
			() => OpenApiClient.Store.fxIdWebFeaturesStoreCreatePaymentEndpoint(paymentRequest)
		);
		const orderContext: IPlatformPurchaseContext = { sku: request.sku, transactionId: payment.TransactionId };
		try {
			const result = await runPlatformPurchaseStep(
				OK_VIA_VK_BRIDGE_PURCHASE,
				"purchase",
				orderContext,
				() => vkBridge.send("VKWebAppShowOrderBox", { type: "item", item: payment.TransactionId })
			);
			const bridgeResult: unknown = result;
			const bridgeSucceeded =
				result.status === "success" ||
				(typeof bridgeResult === "object" &&
					bridgeResult != null &&
					(("status" in bridgeResult && bridgeResult.status === true) ||
						("success" in bridgeResult && bridgeResult.success === true)));
			if (!bridgeSucceeded) {
				throw PlatformPurchaseError.fromError(OK_VIA_VK_BRIDGE_PURCHASE, "purchase", orderContext, result);
			}

			await this.setOptimisticStatus(payment.TransactionId, FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Completed);
			return {
				transactionId: payment.TransactionId,
				stats: {
					currency: payment.OrderProduct.Currency ?? "",
					currencyAmount: payment.OrderProduct.Price ?? 0,
					usdAmount: payment.OrderProduct.UsdPrice ?? 0
				}
			};
		} catch (error: unknown) {
			await this.setOptimisticStatus(payment.TransactionId, FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus.Cancelled);
			throw error;
		}
	}

	/**
	 * Записывает optimistic-статус, не подменяя результат покупки сбоем вспомогательного запроса
	 * @param publicOrderId номер созданного заказа FX ID
	 * @param status статус по результату окна оплаты
	 */
	private async setOptimisticStatus(
		publicOrderId: string,
		status: FxIdDomainModelsDBStoreDBOrderOptimisticUserStatus
	): Promise<void> {
		try {
			await OpenApiClient.Store.fxIdWebFeaturesStoreOrdersSetOptimisticOrderStatusSetOptimisticOrderStatusEndpoint({
				PublicOrderId: publicOrderId,
				Status: status
			});
		} catch (error) {
			log.error("[OKVKBRIDGE] Failed to set optimistic status:", error);
		}
	}
}
